"use client";

import React from "react";
import { useCreateContact } from "@/lib/hooks/api";
import {
  CreateContactForm as CreateContactFormType,
  ContactStatus,
  Client,
} from "@/lib/types/database";
import GenericCsvImportModal from "@/components/shared/GenericCsvImportModal";

interface CsvContactData {
  name: string;
  email: string;
  company_name?: string;
  phone?: string;
  title?: string;
  status?: ContactStatus;
  source?: string;
  notes?: string;
}

interface ContactCsvImportModalProps {
  onSuccess?: () => void;
  triggerButton: React.ReactNode;
  // Optional: Pre-fill CSV with existing contact data (for company-based import)
  existingContact?: Client;
}

const ContactCsvImportModal: React.FC<ContactCsvImportModalProps> = ({
  onSuccess,
  triggerButton,
  existingContact,
}) => {
  const columns = [
    {
      key: "name" as keyof CsvContactData,
      label: "Name",
      required: true,
      description: "Contact's full name",
    },
    {
      key: "email" as keyof CsvContactData,
      label: "Email",
      required: true,
      description: "Contact's email address",
      validate: (value: string) =>
        value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? "A valid email is required" : null,
    },
    {
      key: "company_name" as keyof CsvContactData,
      label: "Company Name",
      required: false,
      description: existingContact?.company_name
        ? `Company name (defaults to: ${existingContact.company_name})`
        : "Company name",
    },
    {
      key: "phone" as keyof CsvContactData,
      label: "Phone",
      required: false,
      description: "Phone number",
    },
    {
      key: "title" as keyof CsvContactData,
      label: "Title",
      required: false,
      description: "Job title",
    },
    {
      key: "status" as keyof CsvContactData,
      label: "Status",
      required: false,
      description: existingContact?.status
        ? `lead, prospect, customer, churned, inactive (defaults to: ${existingContact.status})`
        : "lead, prospect, customer, churned, inactive",
      validate: (value: string) =>
        value && !["lead", "prospect", "customer", "churned", "inactive"].includes(value)
          ? "Status must be one of: lead, prospect, customer, churned, inactive"
          : null,
    },
    {
      key: "source" as keyof CsvContactData,
      label: "Source",
      required: false,
      description: "Lead source",
    },
    {
      key: "notes" as keyof CsvContactData,
      label: "Notes",
      required: false,
      description: "Additional notes",
    },
  ];

  const generateSampleCsv = () => {
    const companyName = existingContact?.company_name || "Doe Corp";
    const status = existingContact?.status || "lead";
    const source = existingContact?.source || "Website";

    return `name,email,company_name,phone,title,status,source,notes
"John Doe","john.doe@example.com","${companyName}","123-456-7890","CEO","${status}","${source}","Initial contact from website form."
"Jane Smith","jane.smith@example.com","${companyName}","098-765-4321","CTO","${status}","${source}","Referred by an existing customer."`;
  };

  const transformRowData = (row: CsvContactData): CreateContactFormType => {
    return {
      name: row.name.trim(),
      email: row.email.trim(),
      company_name: row.company_name?.trim() || existingContact?.company_name || undefined,
      phone: row.phone?.trim() || undefined,
      title: row.title?.trim() || undefined,
      status: row.status || existingContact?.status || "lead",
      source: row.source?.trim() || existingContact?.source || undefined,
      notes: row.notes?.trim() || undefined,
      is_active: true,
      // Copy additional fields from existingContact if available
      // Note: industry is on companies relation, not on contacts directly
      city: existingContact?.city || undefined,
      state: existingContact?.state || undefined,
      country: existingContact?.country || undefined,
    };
  };

  return (
    <GenericCsvImportModal<CsvContactData>
      modalTitle={
        existingContact
          ? `Bulk Import Contacts to ${existingContact.company_name || "Company"}`
          : "Bulk Import Contacts"
      }
      modalDescription={
        existingContact
          ? `Upload a CSV file to create multiple contacts for ${existingContact.company_name || "the company"}. Company information will be pre-filled based on ${existingContact.name}.`
          : "Upload a CSV file to create multiple contacts at once. Download the sample template to see the required format."
      }
      columns={columns}
      generateSampleCsv={generateSampleCsv}
      useCreateHook={useCreateContact}
      transformRowData={transformRowData}
      onSuccess={onSuccess}
      triggerButton={triggerButton}
    />
  );
};

export default ContactCsvImportModal;
